import angular from "angular";

import {
  Dates, HolidayCode, HolidaysLogic, HolidaySpecification, HolidaySpecificationForYear,
  HolidayYear, HolidayYears, Translations, WageBasis, CalendarSeries,
  InputEnumOption,
  HolidayPayCalculation
} from "@salaxy/core";

import { UiCrudHelpers, UiHelpers, SessionService } from "../../services";
import { ApiCrudObjectController } from "../bases";

/**
 * Plain CRUD controller for HolidayYears.
 */
export class HolidayYearCrudController extends ApiCrudObjectController<HolidayYear, never> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "HolidayYears",
    "UiHelpers",
    "$location",
    "$routeParams",
    "UiCrudHelpers",
    "SessionService"
  ];

  /** View type currently shown in the UI */
  public viewType: "overview" | "parameters" | "accrual" | "holidays" | "absenses" = "overview";

  /**
   * If employmentId is set, the controller fetches all the holiday years for this employment relation.
   * The dafault of them will then be set to model and they can be switched easily e.g. using dropdown.
   */
  public employmentHolidayYears: HolidayYear[] = [];

  /** If getHolidayYears() is called, the result will be here */
  public allYears: HolidayYear[];

  /**
   * The date for which the UI is rendered. Default is today.
   * For employment holiday years (WorkerHolidays or CalcWorktime), set this to salary period begin
   * => Specifies the the holiday year to choose by default from all holiday years that Worker has:
   * If the date is Jan-March will show the previous year, April-Dec will show the current year.
   */
  public forDate: string;

  /** Parameters related to holiday report (lomalista). */
  public reportParams: {
    /** Today's date: Set this for testing how user interface is rendered (which view is presented) at different times of year. */
    today: string;
    /** Start date of the view: Can be get/set directly by the view */
    start: string,
    /** Start date of the view: Can be get/set directly by the view */
    end: string,
    /** Start date of the holiday year. Typically, just get: Set by the reportYear setter */
    yearStart: string,
    /** End date of the holiday year. Typically, just get: Set by the reportYear setter */
    yearEnd: string,
    /** Years that can be selected. */
    years: number[];
    /** Underlying field of reportView */
    _view: "summerPeriod" | "winterPeriod" | "otherPeriod" | "list",
    /** Underlying field of reportYear */
    _year: number,
  };

  public isAccrualView = false;

  private _employmentId: string;

  private visualizationCache;
  constructor(
    private fullApi: HolidayYears,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private uiCrudHelpers: UiCrudHelpers,
    private sessionService: SessionService,

  ) { // Dependency injection
    super(fullApi, uiHelpers, $location, $routeParams);
  }

  /** Controller initialization */
  public $onInit() {
    this.forDate = this.forDate || Dates.getToday();
    const thisYear = Dates.getYear(this.forDate);
    this.reportParams = {
      today: this.forDate,
      years: Array.from({ length: 5 }, (v, ix) => thisYear + 1 - ix),
    } as any;
    // Fetching reportView initializes the rest of parameters with default values. Comment for lint if necessary.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const necessaryInitCall = this.reportView;
    super.$onInit();
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/workers",
      detailsUrl: this.detailsUrl || "/workers/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/Holidays.html",
      oDataOptions: {},
    };
  }

  /**
   * Sets the employment ID and if it is different then starts the loading of the Absences object to that employment ID.
   */
  public set employmentId(value: string) {
    if (this._employmentId === value) {
      return;
    }
    this.employmentHolidayYears = [];
    if (!value) {
      if (this._employmentId) {
        this._employmentId = null;
        this.model = null;
        this.reload();
      }
      return;
    }
    this._employmentId = value;
    this.fullApi.getForEmployment(value).then((result) => {
      this.employmentHolidayYears = result;
      this.setYear();
    });
  }
  /** Gets the employment ID */
  public get employmentId() {
    return this._employmentId;
  }

  /** Initializes the holidays for the current Worker */
  public initHolidays(spec: HolidaySpecification,) {
    const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.HolidayYearComponent.dialog.creatingYearHolidays"));
    this.fullApi.initForEmployment(this.employmentId, spec)
      .then((years) => {
        this.employmentHolidayYears = years;
        this.setYear();
        loader.dismiss();
      });
  }

  /**
   * Gets the holiday logic for the given specification.
   * @param spec The specification for which the logic is calculated.
   * @returns The logic object that can be used in the UI.
   */
  public getHolidayLogic(spec: HolidaySpecificationForYear) {
    const logic =  {
      /** If true, has holiday years => A warning for overwrite should be shown. */
      hasData: !!this.current,
      /** True if the accrual is based on law. Ui binds to this. */
      accrualByLaw: !spec.accrualFixed,
      /**
       * Sets the accrualFixed value based on accrualByLaw value
       * @param itemData The data that is being edited (current in view)
       */
      accrualByLawChanged: (itemData: HolidaySpecificationForYear) => {
        itemData.accrualFixed = logic.accrualByLaw ? null : (itemData.code === HolidayCode.TemporaryTimeOff ? 2 : 2.5);
      },
      /**
       * Sets the accrualByLaw value based on accrualFixed value
       * @param itemData The data that is being edited (current in view)
       */
      accrualFixedChanged: (itemData: HolidaySpecificationForYear) => {
        if (!itemData.accrualFixed) {
          itemData.accrualFixed = null; // in case of empty string or 0.
          logic.accrualByLaw = true;
        }
      },
      /**
       * Business logic for field visibility
       * @param itemData The data that is being edited (current in view)
       * @param field Name of the field for which the visibility is calculated.
       */
      getVisibility: (itemData: HolidaySpecificationForYear, field: "accrualFixed" | "compensation" | "bonusMethod" | "bonusPercent" | "startSaldo" | "accruesHolidays") => {
        switch (field) {
          case "accruesHolidays":
          case "startSaldo":
          case "bonusMethod":
            return itemData.code === HolidayCode.Permanent14Days
              || itemData.code === HolidayCode.Permanent35Hours
              || itemData.code === HolidayCode.TemporaryTimeOff
              || itemData.code === HolidayCode.Other
              || itemData.code === HolidayCode.Script;
          case "accrualFixed":
            return itemData.code === HolidayCode.Permanent14Days
            || itemData.code === HolidayCode.Permanent35Hours
            || itemData.code === HolidayCode.TemporaryTimeOff;
          case "compensation":
            return itemData.code === HolidayCode.HolidayCompensation
              || itemData.code === HolidayCode.HolidayCompensationIncluded
              ;
          case "bonusPercent":
            return itemData.code === HolidayCode.Permanent14Days
              || itemData.code === HolidayCode.Permanent35Hours
              || itemData.code === HolidayCode.TemporaryTimeOff
              || itemData.code === HolidayCode.Other
              || itemData.code === HolidayCode.HolidayCompensation
              || itemData.code === HolidayCode.HolidayCompensationIncluded
              || itemData.code === HolidayCode.Script
              ;
          default:
            return true;
        }
      },
      getholidayPayCalculationOptions:(itemData: HolidaySpecificationForYear): InputEnumOption[] => {
        return this.uiHelpers.cache(this, "holidayPayCalculationOptions", () => {
          switch (itemData?.code) {
            case HolidayCode.Permanent14Days:
              if(itemData.wageBasis === WageBasis.Monthly) {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.normalMonthlyPay.label"), value: HolidayPayCalculation.NormalMonthlyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              } else {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageHourlyPay.label"), value: HolidayPayCalculation.AverageHourlyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageDailyPay.label"), value: HolidayPayCalculation.AverageDailyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              }
            case HolidayCode.Permanent35Hours:
              if(itemData.wageBasis === WageBasis.Monthly) {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.normalMonthlyPay.label"), value: HolidayPayCalculation.NormalMonthlyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              } else {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              }
            case HolidayCode.TemporaryTimeOff:
              return [
                {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.holidayCompensation.label"), value: HolidayPayCalculation.HolidayCompensation},
                {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
              ];
              break;
            case HolidayCode.Other:
            case HolidayCode.Script:
                if(itemData.wageBasis === WageBasis.Monthly) {
                  return [
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.normalMonthlyPay.label"), value: HolidayPayCalculation.NormalMonthlyPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.holidayCompensation.label"), value: HolidayPayCalculation.HolidayCompensation},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                  ];
                } else {
                  return [
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageHourlyPay.label"), value: HolidayPayCalculation.AverageHourlyPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageDailyPay.label"), value: HolidayPayCalculation.AverageDailyPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.holidayCompensation.label"), value: HolidayPayCalculation.HolidayCompensation},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                  ];
                }
            default:
              return [];
          }
        });
      }
    };
    return logic;
  }

  /** Shows the Init holidays dialog for initializing the holiday years for the given worker */
  public showInitHolidays() {
    if (!this.employmentId) {
      throw Error("Employment ID not defined in showInitHolidays().");
    }
    const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.HolidayYearComponent.dialog.loadingDefaults"));
    this.fullApi.getDefaultSpecForEmployment(this.employmentId).then((defaults) => {

      loader.dismiss();
      const logic = this.getHolidayLogic(defaults);

      this.uiHelpers.openEditDialog("salaxy-components/worker/holidays/WorkerHolidaysInitDialog.html", defaults, logic, "lg")
        .then((result) => {
          if (result.action === "ok") {
            this.initHolidays(result.item);
          }
        });
    });
  }

  /** Gets the visualisation data for the selected holiday year. */
  public getYearVisualisation() {
    if (!this.current) {
      return null;
    }
    if (!this.visualizationCache || this.visualizationCache.year !== this.current.year || this.visualizationCache.employmentId !== this.current.employmentId) {
      this.visualizationCache = HolidaysLogic.getYearVisualisation(this.current);
    }
    return this.visualizationCache;
  }

  /** Gets a total days calculation for different types. */
  public getTotalDays(type: "all" | "summer" | "winter" | "holidaysSaldoEnd" | "holidaysSaldoStart" = "all", year: HolidayYear = null) {
    year = year || this.current;
    return HolidaysLogic.getPlannedLeavesCount(year, type);
  }

  /**
   * Gets items visibility based on properties of the selected holiday year.
   * @param elements Logical name of group of items in teh view.
   */
  public getVisibility(elements: "accrual" | "compensation" | "hourly") {
    const code = this.current.spec.code;
    switch (elements) {
      case "accrual":
        return code === HolidayCode.Permanent14Days
          || code === HolidayCode.Permanent35Hours
          || code === HolidayCode.TemporaryTimeOff
          || code === HolidayCode.Other
          || code === HolidayCode.Script
          ;
      case "hourly":
        return this.current.spec.wageBasis === WageBasis.Hourly
          || this.current.spec.wageBasis === WageBasis.PerformanceBased
          ;
      case "compensation":
        return code === HolidayCode.HolidayCompensation
          || code === HolidayCode.HolidayCompensationIncluded
          ;
      default:
        return true;
    }
  }

  /** For the reporting purposes, gets or sets the holiday year as year number. */
  public get reportYear(): number {
    if (!this.reportParams._year) {
      let year = Dates.getYear(this.reportParams.today || "today");
      if (Dates.getMonth(this.reportParams.today || "today") < 3) {
        year--;
      }
      this.reportYear = year; // Call the setter to init related values
    }
    return this.reportParams._year;
  }
  public set reportYear(value: number) {
    value = Math.round(Number(value));
    if (!value || value < 2018 || value > 2100) {
      return;
    }
    this.reportParams._year = value;
    if (this.reportView !== "otherPeriod") {
      this.reportParams.yearStart = value + "-01-01";
      this.reportParams.yearEnd = (value + 1) + "-04-30";
    } else {
      this.reportParams.yearStart = value + "-05-01";
      this.reportParams.yearEnd = (value + 1) + "-04-30";
    }
  }

  /**
   * Gets or sets the report view that is currently shown.
   */
  public get reportView(): "summerPeriod" | "winterPeriod" | "otherPeriod" | "list" {
    if (!this.reportParams._view) {
      // Use the setter for setting the defaault value so that dependent fields are also set
      const month = Dates.getMonth(this.reportParams.today || "today");
      const year = Dates.getYear(this.reportParams.today || "today");
      if (year === this.reportYear) {
        if (month < 10) { this.reportView = "summerPeriod"; }
        else { this.reportView = "winterPeriod"; }
      } else if (year - 1 === this.reportYear) {
        if (month < 4) { this.reportView = "winterPeriod"; } // Current holidays
        else { this.reportView = "list"; }
      } else {
        this.reportView = "list";
      }
    }
    return this.reportParams._view;
  }
  public set reportView(value: "summerPeriod" | "winterPeriod" | "otherPeriod" | "list") {
    this.reportParams._view = value;
    switch (value) {
      case "summerPeriod":
        this.reportParams.start = this.reportYear + "-05-01";
        this.reportParams.end = this.reportYear + "-09-30";
        break;
      case "winterPeriod":
        this.reportParams.start = this.reportYear + "-10-01";
        this.reportParams.end = this.reportYear + 1 + "-04-30";
        break;
      case "otherPeriod":
        this.reportParams.start = this.reportYear + "-01-01";
        this.reportParams.end = this.reportYear + 1 + "-04-30";
        break;
      default:
        this.reportParams.start = this.reportParams.yearStart;
        this.reportParams.end = this.reportParams.yearEnd;
    }
  }

  /**
   * Returns missing holiday year.
   */
  public get missingHolidayYear() {
    if (!this.employmentHolidayYears || this.employmentHolidayYears.length == 0) {
      // Not holiday years in use.
      return null;
    }
    const today = Dates.getToday();
    const missingYear = 1 + (Dates.getMonth(today) > 3 ? Dates.getYear(today) : Dates.getYear(today) - 1);
    if (this.employmentHolidayYears.find((x) => x.year == missingYear)) {
      return null;
    }

    return missingYear;
  }

  /**
   * Saves a holiday year based on calendar input and updates the value in allYears property.
   */
  public saveYear(itemToSave: HolidayYear): Promise<void> {
    const loader = this.uiHelpers.showLoading(`${Translations.get("SALAXY.NG1.HolidayYearComponent.dialog.saving")}...`);
    return this.api.save(itemToSave).then((savedValue) => {
      angular.copy(savedValue, itemToSave);
      loader.dismiss();
      return;
    });
  }

  /**
   * Show an edit dialog for the holiday year.
   * @param year The holiday year to update.
   */
  public showEditDialog(year: HolidayYear): void {
    /* HACK: The edit dialog is not optimal, as it shows the entire edit worker dialog. Should be a separate edit holidays / holiday periods dialog. */
    this.uiCrudHelpers.openEditEmploymentDialog(year.employmentId, "default", "holidays").then((result) => {
      if (result.action === "ok") {
        this.getHolidayYears(true);
      }
    });
  }

  /**
   * Shows worker selection dialog and after that opens selected workers employment dialog.
   */
  public showAddWorker() {
    const workerList = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/WorkerSelect.html",
      workerList,
      {
        title: "SALAXY.NG1.PayrollComponent.validate.addWorker",
      }).then((result) => {
        if (result.action === "ok" && result.item.length == 1) {
          this.uiCrudHelpers.openEditEmploymentDialog(result.item[0].id, "default", "holidays").then((result) => {
            if (result.action === "ok") {
              this.getHolidayYears(true);
            }
          });
        }
      });
  }

  /**
   * Gets the full holiday years for all Workers for reporting purposes
   * @param keepView If true, does not reset the view based on the incoming data.
   * If true, you should make sure the year does not change because then view will be invalid for the year
   * I.e. should be true only for reload scenarios.
   */
  public getHolidayYears(keepView = false) {
    this.fullApi.getForYear(this.reportYear).then((result: HolidayYear[]) => {
      this.allYears = result;
      if (!keepView) {
        this.reportParams._view = null;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const getViewForInitAfterYearChange = this.reportView;
      }
    });
  }

  /** Maps an array of HolidayYear to calendar series. */
  public mapToCalendar = (years: HolidayYear[]): CalendarSeries[] => {
    return years.map((hy) => {
      return {
        key: hy.id,
        title: hy.workerSnapshot.displayName,
        description: `${Translations.get("SALAXY.NG1.HolidayYearComponent.planned")} ${this.getTotalDays("all", hy)}${Translations.get("SALAXY.NG1.HolidayYearComponent.date")}, ${Translations.get("SALAXY.NG1.HolidayYearComponent.accumulation")} ${hy.accrual.endSaldo}`,
        avatar: hy.workerSnapshot,
        data: hy,
        events: hy.leaves.planned.map((leave) => ({
          start: leave.period.start,
          end: leave.period.end,
          summary: leave.notes || `${Translations.get("SALAXY.NG1.HolidayYearComponent.holiday")} ${leave.period.daysCount} ${Translations.get("SALAXY.NG1.HolidayYearComponent.days")}`,
          data: leave,
        }
        )),
      };
    });
  };

  /**
   * Returns true, if two objects are different.
   * @param obj1 First object to compare
   * @param obj2 Second object to compare
   */
  public isChanged(obj1, obj2): boolean {
    return !angular.equals(obj1, obj2);
  }

  /**
   * Sets the view type for the holiday year.
   * @param isAccrualView  If true, the view is set to this.currentYear +1, otherwise to this.currentYear.
   */
  public setAccrualView(isAccrualView: boolean): void {
    this.isAccrualView = isAccrualView;
  }

  /**
   * Sets the correct holiday year for the view based on the current date.
   * This is for planning/consuming holidays.
   */
  public setYear(): void {
    const date = this.forDate;
    const holidayYear = date > Dates.getDate(Dates.getYear(date), 4, 30) ? Dates.getYear(date) : Dates.getYear(date) - 1;
    this.model = this.employmentHolidayYears.find((x) => x.year === holidayYear) || this.employmentHolidayYears[0];
  }

  /** Recalculate holiday pay for the current holiday year */
  public recalculateHolidayPay(): Promise<HolidayYear> {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    return this.fullApi.recalculateEmploymentHolidayPay(this.current.year, this.current.employmentId).then( () => {
      loading.dismiss();
      return this.reload();
    });
  }

  /** Recalculate holiday pay for all employments in the holiday year */
  public recalculateAllHolidayPay() {
    return this.uiHelpers.showConfirm("Vuosiloma-ansion laskenta",
      `Haluatko varmasti laskea uudestaan vuosiloma-ansion kaikille työntekijöille lomavuodelle ${this.current.year}? Tämä voi kestää hetken.`).then( (result) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          return this.fullApi.recalculateHolidayPay(this.current.year).then( () => {
            loading.dismiss();
            return this.uiHelpers.showAlert("Vuosiloma-ansion laskenta", "Vuosiloma-ansion laskenta on käynnistetty. Laskenta tapahtuu taustalla, ja se voi kestää jonkin aikaa.");
          });
        }
        else {
          return result;
        }
      });
  }
}
